import { PaginationArrowRight } from 'assets/icons/PaginationArrowRight'
import { useMemo } from 'react'
import styled, { css } from 'styled-components'
import { tokens } from 'styling/tokens'
import { usePagination } from './usePagination'

const getPages = (pageCount: number, currentPage: number) => {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1)
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, '...', pageCount]
  }

  if (currentPage >= pageCount - 3) {
    return [1, '...', pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1, pageCount]
  }

  return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', pageCount]
}

export const Pagination = ({ itemCount, limit }: { itemCount: number; limit: number }) => {
  const { currentPage, setCurrentPage } = usePagination()
  const pageCount = Math.ceil(itemCount / limit)
  const arrayToDisplay = useMemo(() => getPages(pageCount, currentPage), [pageCount, currentPage])

  if (pageCount < 2) return <></>

  return (
    <Container>
      <ArrowButton
        direction='left'
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(currentPage - 1)}
      >
        <PaginationArrowRight />
      </ArrowButton>
      <PagesContainer>
        {arrayToDisplay.map((page: number | string, index: number) => (
          <Button
            key={index + page.toString()}
            onClick={() => (typeof page === 'number' ? setCurrentPage(page) : null)}
            active={currentPage === page}
          >
            {page}
          </Button>
        ))}
      </PagesContainer>
      <ArrowButton
        direction='right'
        disabled={currentPage === pageCount}
        onClick={() => setCurrentPage(currentPage + 1)}
      >
        <PaginationArrowRight />
      </ArrowButton>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const PagesContainer = styled.div`
  border: 1px solid ${tokens.border.hairline};
  background: ${tokens.surface.glass};
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${tokens.text.primary};
`

const Button = styled.button<{ active?: boolean }>`
  background: ${(props) => (props.active ? tokens.accent.soft : 'none')};
  border: none;
  cursor: pointer;
  color: ${(props) => (props.active ? tokens.accent.base : tokens.text.secondary)};
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  font-weight: 600;

  &:hover {
    color: ${(props) => (props.active ? tokens.accent.base : tokens.text.primary)};
  }
`

const ArrowButton = styled.button<{ direction: 'left' | 'right' }>`
  border: 1px solid ${tokens.border.hairline};
  color: ${tokens.text.secondary};
  cursor: pointer;
  background: none;
  border-radius: 0.5rem;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${tokens.accent.base};
    color: ${tokens.accent.base};
  }

  &:disabled {
    border-color: ${tokens.border.hairline};
    color: ${tokens.text.muted};
    cursor: not-allowed;
  }

  ${(props) =>
    props.direction === 'left' &&
    css`
      transform: rotate(180deg);
    `}
`
